import { Link, Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/lib/auth";
import { LayoutDashboard, Boxes, Building, Newspaper, MessageSquare, Mail, LogOut, Anchor, Menu, X, UserPlus } from "lucide-react";

const MMP_API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/solutions", label: "Solutions", icon: Boxes },
  { to: "/admin/projects", label: "Projects", icon: Building },
  { to: "/admin/blog", label: "Blog Posts", icon: Newspaper },
  { to: "/admin/leads", label: "Leads", icon: MessageSquare },
  { to: "/admin/applications", label: "Careers · Submissions", icon: UserPlus, badgeKey: "careers" },
  { to: "/admin/newsletter", label: "Newsletter", icon: Mail },
];

export default function AdminLayout() {
  const { user, ready, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [careersCount, setCareersCount] = useState(null);
  const loc = useLocation();

  useEffect(() => setOpen(false), [loc.pathname]);

  useEffect(() => {
    if (!user) return;
    const load = () => axios.get(`${MMP_API}/admin-mmp/stats`).then((r) => setCareersCount(r.data.total || 0)).catch(() => {});
    load();
    const id = setInterval(load, 20000); // poll every 20s
    return () => clearInterval(id);
  }, [user, loc.pathname]);

  if (!ready) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[color:var(--mia-grey)] flex" data-testid="admin-layout">
      <aside className={`fixed lg:sticky top-0 h-screen w-72 bg-[color:var(--mia-navy)] text-white z-40 transform transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="p-6 flex items-center gap-3 border-b border-white/10">
          <div className="w-10 h-10 rounded-full bg-white/10 text-[color:var(--mia-gold)] flex items-center justify-center"><Anchor className="w-5 h-5" /></div>
          <div className="leading-tight">
            <div className="font-display font-black text-white text-sm">MIA · ADMIN</div>
            <div className="text-[0.62rem] font-bold text-[color:var(--mia-gold)] tracking-[0.28em]">MARINE INFRASTRUCTURE</div>
          </div>
        </div>
        <nav className="p-4 space-y-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `flex items-center justify-between gap-3 px-4 py-3 rounded-lg text-sm font-semibold transition-colors ${isActive ? "bg-[color:var(--mia-gold)] text-[color:var(--mia-navy)]" : "text-white/80 hover:bg-white/5 hover:text-white"}`} data-testid={`admin-nav-${n.label.toLowerCase().replace(/[·\s]+/g, "-")}`}>
              <span className="inline-flex items-center gap-3"><n.icon className="w-4 h-4" /> {n.label}</span>
              {n.badgeKey === "careers" && careersCount !== null && careersCount > 0 && (
                <span className="text-[10px] font-bold bg-[color:var(--mia-green)] text-[color:var(--mia-navy)] px-2 py-0.5 rounded-full min-w-[22px] text-center" data-testid="nav-careers-badge">{careersCount}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          <div className="text-xs text-white/60 mb-3">{user.email}</div>
          <button onClick={logout} className="w-full mia-btn mia-btn-ghost justify-center" data-testid="admin-logout">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
          <Link to="/" className="block text-center text-xs text-white/60 mt-4 hover:text-[color:var(--mia-gold)]">← Back to public site</Link>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <div className="lg:hidden sticky top-0 bg-white border-b border-slate-100 z-30">
          <div className="flex items-center justify-between p-4">
            <button onClick={() => setOpen(!open)} className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center" data-testid="admin-sidebar-toggle">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <div className="font-display font-bold text-[color:var(--mia-navy)]">MIA Admin</div>
            <div className="w-10" />
          </div>
        </div>
        <main className="p-6 lg:p-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
